import type { TAvatar } from "@/types/TAvatar";
import { X, Pencil } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface AvatarItemProps {
  avatar: TAvatar;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  allAvatars?: TAvatar[];
}

export function AvatarItem({ avatar, onRemove, onEdit, allAvatars = [] }: AvatarItemProps) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 });
  const itemRef = useRef<HTMLDivElement>(null);

  const calculateActualPercentage = () => {
    const totalWeight = allAvatars.reduce((sum, a) => sum + (a.weight ?? 1.0), 0);
    const avatarWeight = avatar.weight ?? 1.0;
    return totalWeight > 0 ? (avatarWeight / totalWeight) * 100 : 0;
  };

  const percentage = calculateActualPercentage();
  const isDisabled = (avatar.weight ?? 1.0) === 0;

  const handleRemove = (e: React.MouseEvent) => {
    if (window.confirm(t("dialogs.confirmRemove", { name: avatar.avatar_name }))) {
      e.stopPropagation();
      e.preventDefault();
      onRemove(avatar.avatar_id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEdit(avatar.avatar_id);
  };

  useEffect(() => {
    if (showPreview && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setPreviewPosition({
        top: rect.bottom + 10,
        left: rect.left,
      });
    }
  }, [showPreview]);

  return (
    <div
      ref={itemRef}
      className={`group relative flex w-full select-none flex-col py-3 px-4 text-sm hover:bg-accent/50 transition-colors ${isDisabled ? "opacity-50" : ""}`}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
    >
      <div className="flex w-full justify-between items-center">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-medium truncate">{avatar.avatar_name}</span>
          <span className={`text-xs font-semibold shrink-0 ${isDisabled ? "text-muted-foreground" : "text-primary"}`}>
            {percentage.toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center shrink-0">
          <div
            onMouseUp={handleEdit}
            className="opacity-0 group-hover:opacity-100 hover:opacity-100 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer"
            aria-label={t("dialogs.editAvatar") + `: ${avatar.avatar_name}`}
          >
            <Pencil size={14} />
          </div>
          <div
            onMouseUp={handleRemove}
            className="ml-auto opacity-0 group-hover:opacity-100 hover:opacity-100 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer"
            aria-label={t("common.remove") + `: ${avatar.avatar_name}`}
          >
            <X size={14} />
          </div>
        </div>
      </div>

      <div className="mt-1.5 w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${isDisabled ? "bg-muted-foreground/30" : "bg-primary"}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {showPreview && avatar.preview_image_url && document.body && createPortal(
        <div
          className="fixed z-[100] bg-popover border rounded-md shadow-md p-3 w-64"
          style={{
            top: `${previewPosition.top}px`,
            left: `${previewPosition.left}px`,
          }}
        >
          <img
            src={avatar.preview_image_url}
            data-src={avatar.preview_low_res_image_url}
            alt={t("dialogs.previewOf", { name: avatar.avatar_name })}
            className="w-full h-auto rounded"
            onError={(e) => console.error("Failed to load image:", e)}
          />
          <p className="text-xs mt-2 text-center font-medium">{avatar.avatar_name}</p>
          {avatar.voice_id && (
            <p className="text-xs text-center text-muted-foreground">{avatar.voice_id}</p>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
